import type {ReactElement} from 'react';
import React from 'react';
import styles from './poster-editor-multipage-popup.module.scss';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {getAssetUrl} from '@Utils/s3.util';
import {useShowContextualSkeletons} from '@Hooks/poster-editor/useShowContextualSkeletons';
import {markMultipageOnboardingAsSeen, showMultipageOnboarding} from '@Components/poster-editor/library/poster-editor-library';
import {isWebmSupported} from '@Utils/browser.util';
import {getHasShownOnboardingPopup, setHasShownOnboardingPopup} from '@Components/poster-editor/poster-editor-reducer';

const POPUP_GAP = 12;

export function PosterEditorMultipagePopup(): ReactElement | null {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [isDesktop, setIsDesktop] = React.useState(true);
  const [showPopup, setShowPopup] = React.useState(false);
  const isPosterLoading = useShowContextualSkeletons();

  React.useEffect((): void => {
    if (isPosterLoading) {
      return;
    }

    const pageOrder = window.posterEditor?.whiteboard?.pages?.pageOrder;

    if (pageOrder && pageOrder.length > 1) {
      return;
    }

    void showMultipageOnboarding().then((shouldShow): void => {
      if (!shouldShow || getHasShownOnboardingPopup(window.PMW.redux.store.getState())) {
        return;
      }
      window.PMW.redux.store.dispatch(setHasShownOnboardingPopup(true));
      setShowPopup(true);
    });
  }, [isPosterLoading]);

  const onClose = (): void => {
    setShowPopup(false);
    void markMultipageOnboardingAsSeen();
  };

  React.useLayoutEffect(() => {
    if (!showPopup) {
      return;
    }

    const updateVariant = (): void => {
      if (!containerRef.current?.parentElement) {
        return;
      }
      const spaceOnLeft = containerRef.current.parentElement.getBoundingClientRect().left;
      setIsDesktop(spaceOnLeft >= containerRef.current.offsetWidth + POPUP_GAP);
    };

    updateVariant();
    window.addEventListener('resize', updateVariant);
    return () => {
      window.removeEventListener('resize', updateVariant);
    };
  }, [showPopup]);

  if (!showPopup) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return (
      <div className={`spacing-p-l-4 spacing-p-r-4 spacing-p-t-2 flex-row-align-center ${styles.header}`}>
        <Text className="_full-width" val={window.i18next.t('pmwjs_new_multipage')} size={TextSize.MD} bold />
        <Icon className={styles.icon} onClick={onClose} icon="icon-close" size={IconSize.SIZE_ICON_16} shape={IconShape.SQUARE} type={IconType.GHOST} />
      </div>
    );
  };

  const getPanel = (): ReactElement => {
    return (
      <div className={`${styles.panel} flex-v-row flex-align-items-center`}>
        <video
          className={`_full-width ${styles.video}`}
          src={getAssetUrl(`poster-editor-multipage-onboarding.${isWebmSupported() ? 'webm' : 'mp4'}`)}
          autoPlay
          loop
          muted
          playsInline
        />
        <Text className="spacing-m-t-3" val={window.i18next.t('pmwjs_multipage_onboarding_description')} size={TextSize.SM} />
      </div>
    );
  };

  return (
    <div ref={containerRef} className={`flex-v-row radius-8 border-s-standard shadow-floating-md _fit-height ${styles.container} ${isDesktop ? styles.desktop : styles.tablet}`}>
      {getHeader()}
      {getPanel()}
      <svg width="22" height="16" viewBox="0 0 22 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={isDesktop ? styles.arrowBottomRight : styles.arrowBottomCenter}>
        <path d="M14.1485 11.0939C12.5617 13.4347 9.11315 13.4347 7.52642 11.0939L0.00658417 0.000106812H21.6683L14.1485 11.0939Z" fill="white" />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M14.6106 10.8932C12.8327 13.6126 8.84682 13.6092 7.07351 10.8867L0 0.0269756L1.12534 0.0271587L7.91144 10.3409C9.29068 12.4584 12.3908 12.4611 13.7736 10.3459L20.5767 0.032527H21.7113L14.6106 10.8932Z"
          fill="#E6E6EC"
        />
      </svg>
    </div>
  );
}
